import { OrganizationModel } from "./Organization";
import {
    UserDismissableDialog,
    User as UserType,
    FeatureFlagSet,
    Workspace,
} from "@/types/generated/management-api-types";
import { assertNotUndefined } from "@/util/typeguards.ts";

export class User implements UserType {
    readonly id: string;
    readonly cognitoId: string;
    readonly picture?: string;
    readonly name: string;
    readonly email: string;
    readonly featureFlags: FeatureFlagSet;
    readonly dismissedDialogs: UserDismissableDialog[];
    /**
     * The dashboard layout per workspace id, in the shape features/Dashboard/
     * layout.ts reads. Unknown here: the Api keeps it, the dashboard owns it.
     */
    readonly dashboardLayouts: UserType["dashboardLayouts"];
    // readonly userOrganization: UserOrganizationModel;
    readonly createdAt: string;
    readonly currentOrganization: OrganizationModel;
    readonly managementOrganization?: UserType["managementOrganization"];
    readonly timezone?: string;
    readonly intercomUserHash?: string;
    readonly currentUserPermissionsType: UserType["currentUserPermissionsType"];
    readonly currentUserActiveInOrganization: boolean;
    readonly isUtmScout: boolean;
    readonly currentOrganizationSetOnCreate: boolean;
    // Super admin
    readonly sa: boolean;
    // The account is on its way out, and stays readable until deletion finishes.
    // Carried because this class mirrors the Api's user; nothing in the app reads
    // it yet, and the backend is what refuses the session (SessionStarter).
    readonly deleting: boolean;

    private constructor(args: {
        id: string;
        cognitoId: string;
        name: string;
        email: string;
        featureFlags: FeatureFlagSet;
        picture?: string;
        dismissedDialogs?: UserDismissableDialog[];
        dashboardLayouts?: UserType["dashboardLayouts"];
        timezone?: string;
        createdAt: string;
        intercomUserHash?: string;
        currentOrganization: UserType["currentOrganization"];
        currentUserPermissionsType: UserType["currentUserPermissionsType"];
        currentUserActiveInOrganization: boolean;
        managementOrganization?: UserType["managementOrganization"];
        isUtmScout: boolean;
        currentOrganizationSetOnCreate?: boolean;
        sa: boolean;
        deleting?: boolean;
    }) {
        this.id = args.id;
        this.email = args.email;
        this.cognitoId = args.cognitoId;
        this.featureFlags = args.featureFlags;
        this.name = args.name;
        this.email = args.email;
        this.picture = args.picture;
        this.currentUserPermissionsType = args.currentUserPermissionsType;
        this.currentUserActiveInOrganization =
            args.currentUserActiveInOrganization;
        this.currentOrganization = OrganizationModel.create(
            args.currentOrganization,
        );
        this.managementOrganization = args.managementOrganization;
        this.dismissedDialogs = args.dismissedDialogs || [];
        this.dashboardLayouts = args.dashboardLayouts ?? [];
        this.timezone = args.timezone;
        this.createdAt = args.createdAt;
        this.intercomUserHash = args.intercomUserHash;
        this.isUtmScout = args.isUtmScout;
        this.currentOrganizationSetOnCreate =
            args.currentOrganizationSetOnCreate ?? false;
        this.sa = args.sa;
        this.deleting = args.deleting ?? false;
    }

    static fromApiUser(userData: UserType): User {
        return new this({
            id: userData.id,
            name: userData.name,
            cognitoId: userData.cognitoId,
            email: userData.email,
            picture: userData.picture,
            currentUserPermissionsType: userData.currentUserPermissionsType,
            dismissedDialogs: userData.dismissedDialogs,
            dashboardLayouts: userData.dashboardLayouts,
            // @ts-expect-error Nelmio api doc cannot output the correct
            // object structure — the real shape is a flat kebab-keyed dict
            // (see FeatureFlagManagerProvider), so `{}` is the only safe default.
            featureFlags: userData.featureFlags ?? {},
            timezone: userData.timezone,
            createdAt: userData.createdAt,
            intercomUserHash: userData.intercomUserHash,
            currentUserActiveInOrganization:
                userData.currentUserActiveInOrganization,
            currentOrganization: userData.currentOrganization,
            managementOrganization: userData.managementOrganization,
            isUtmScout: userData.isUtmScout,
            currentOrganizationSetOnCreate:
                userData.currentOrganizationSetOnCreate,
            sa: userData.sa,
            deleting: userData.deleting,
        });
    }

    public getData(): User {
        return JSON.parse(JSON.stringify(this));
    }

    public hasDialog(dialogId: UserDismissableDialog): boolean {
        return !this.dismissedDialogs.includes(dialogId);
    }

    public getPermissionsType(): UserType["currentUserPermissionsType"] {
        return this.currentUserPermissionsType;
    }

    public hasAdminPermissions() {
        return this.hasPermissionType("admin");
    }

    public hasEditorPermissions() {
        return this.hasPermissionType("editor");
    }

    public isOrganizationOwner() {
        return this.hasPermissionType("owner");
    }

    public hasPermissionType(
        requestedType: UserType["currentUserPermissionsType"],
    ) {
        const permissionsType = this.getPermissionsType();

        if (!permissionsType) {
            throw new Error("Unexpectedly no permission type");
        }

        if (permissionsType === "owner") {
            return true;
        }

        // User is not owner
        if (requestedType === "owner") {
            return false;
        }

        if (permissionsType === "admin") {
            return true;
        }

        return requestedType === permissionsType;
    }

    public getTimezoneSafe(): string {
        return this.timezone || "UTC";
    }

    public getCurrentOrganization() {
        return this.currentOrganization;
    }

    public isManager(): boolean {
        return !!this.managementOrganization;
    }

    public isSuperAdmin(): boolean {
        return this.sa;
    }

    /**
     * Whether the organization signed up for an Icp audit and has not started
     * its trial: it has the audit's screens and nothing else.
     */
    public isAudit(): boolean {
        return this.currentOrganization.plan === "audit";
    }

    public managesOrganization(
        organization?: Workspace["organization"],
    ): boolean {
        if (!organization?.manager) {
            return false;
        }

        if (!this.isManager()) {
            return false;
        }

        assertNotUndefined(this.managementOrganization);
        assertNotUndefined(organization.manager);

        return organization.manager.id === this.managementOrganization.id;
    }
}
